import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog } from 'primereact/dialog';
import { Button } from '../../../../components/ui/Button';

import CampaignsList from './CampaignsList';
import { useAssignCampaignDevices, useGetAllCampaigns, useGetCampaigns } from '../../../../queries';
import { RadioButton } from 'primereact/radiobutton';

import { useSelector, useDispatch } from 'react-redux';
import { toggleDialog } from '../../../../redux/Slices/Global/globalSlice';
import { setSelectedCampaign, setSelectedDevice } from '../../../../redux/Slices/deviceSlice';
import PopupScrollContainer from '../../../SharedComponents/PopupScrollContainer';
import { Message } from 'primereact/message';
import { isEmpty } from 'lodash';
import { Tooltip } from 'primereact/tooltip';
import { Checkbox } from 'primereact/checkbox';
import { setLastElement, setParams } from '../../../../redux/Slices/popupScrollSlice';
import PopupScrollSearchInput from '../../../SharedComponents/AddContentPopup/PopupScrollSearchInput';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { ProgressSpinner } from 'primereact/progressspinner';
import { formatDuration } from '../../../../config/global';

// Legacy S3 hosts → CDN. Pure helper, module-level so it has a stable identity.
const normalizeCdn = (url) => {
    if (!url) return url;
    const OLD1 = "http://storage-popuppush-copy.s3.eu-west-1.amazonaws.com";
    const OLD2 = "http://storage-popuppush.s3.me-south-1.amazonaws.com";
    const NEW = "https://storage.popuppush.com";
    if (url.includes(OLD1)) return url.replace(OLD1, NEW);
    if (url.includes(OLD2)) return url.replace(OLD2, NEW);
    return url;
};

/**
 * One campaign row. Module-level + React.memo so selecting a campaign only
 * re-renders the previously-selected and newly-selected rows — every other
 * row (and the scroll container itself) stays mounted, so the scroll
 * position is preserved. `onSelect` must be a stable reference.
 */
const CampaignRow = React.memo(function CampaignRow({ item, selected, onSelect, t }) {
    const id = String(item.id);
    const rawThumb = item?.cover?.image_thumb ?? item?.cover?.image_path ?? null;
    const thumb = rawThumb && rawThumb !== "loading" ? normalizeCdn(rawThumb) : null;
    const isColor = item.cover?.type === "COLOR";

    /* ⚠️ IT USED TO WARN AND LET YOU CLICK ANYWAY. The row showed "Publish
       Required" and stayed selectable, so the warning was decoration — the
       server would have accepted the assignment and the screen would have
       played nothing. A warning that does not stop you is the same lie as a
       badge that reports the wrong thing.

       Blocked on CONTENT, not status: is_playable means "has a published MAIN
       row", resolved server-side by the same check the assign endpoints now
       enforce. Gating on status would have hidden every wizard show, which is
       most of what a venue uses. Older API responses omit the flag, and those
       stay selectable rather than being silently disabled. */
    const blocked = item.is_playable === false;

    return (
        <div
            onClick={() => { if (!blocked) onSelect(id); }}
            aria-disabled={blocked}
            // Prevent the click from focusing the radio input: a focused input near
            // the bottom makes the browser scroll-into-view, nudging the scroll
            // container so the bottom sentinel crosses the IntersectionObserver's
            // rootMargin and spuriously fires fetchNextPage (the "last ~3 items"
            // jump). preventDefault on mousedown stops the focus (and its scroll)
            // while leaving the click selection intact; keyboard focus is unaffected.
            onMouseDown={(e) => e.preventDefault()}
            className={`w-100 flex items-center gap-3 rounded-lg px-3 py-2 transition-shadow border-2
               ${blocked ? "opacity-60 cursor-not-allowed border-gray-200" : "cursor-pointer"}
               ${selected ? "pp-selected-row" : blocked ? "" : "border-gray-200 hover:shadow-md"}`}
        >
            <div className="d-flex align-items-center gap-3 flex-1 min-w-0">
                {/* Thumbnail — fixed 48x48, rounded, object-cover, with a tinted
                    placeholder layer behind the image (icon / initial fallback). */}
                <div className="position-relative flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                    <div
                        className="position-absolute inset-0 rounded-lg d-flex align-items-center justify-content-center fw-semibold"
                        style={{
                            fontSize: '1rem',
                            backgroundColor: isColor ? (item.cover?.color || '#8e90f5') : '#eef1ff',
                            color: isColor ? '#ffffff' : '#8e90f5',
                        }}
                    >
                        {isColor
                            ? <i className="fa-solid fa-film" />
                            : (item.name?.trim()?.charAt(0)?.toUpperCase() || <i className="fa-solid fa-image" />)}
                    </div>
                    {thumb && item.cover && !isColor && (
                        <LazyLoadImage
                            src={thumb}
                            alt={item.name}
                            className="position-absolute inset-0 w-full h-full object-cover rounded-lg"
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                        />
                    )}
                </div>

                {/* Name + optional status — up to 2 lines, logical start-align. */}
                <div className="min-w-0 d-flex flex-column justify-content-center" style={{ textAlign: 'start' }}>
                    <span
                        className="fs-6 fw-medium"
                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.3, textAlign: 'start' }}
                    >
                        {item.name}
                    </span>
                    {blocked && (
                        <div className="gap-1 text-muted text-[12px] flex justify-start items-center mt-1">
                            <i className="draftedItem cursor-default fa-solid fa-circle-exclamation" data-pr-position="bottom" data-pr-tooltip={t('devices.assign_publish_tooltip')}></i>
                            <div className="draftedItem cursor-default text-truncate" data-pr-position="bottom" data-pr-tooltip={t('devices.assign_publish_tooltip')}>
                                {t('devices.assign_publish_required')}
                            </div>
                        </div>
                    )}
                    <Tooltip target=".draftedItem" showDelay={100} className="fs-8" />
                </div>
            </div>

            {/* Radio — trailing side, vertically centered, RTL-safe via ms-auto.
                Visual + keyboard control; selection is driven by the row onClick
                (onChange kept for keyboard/space activation, idempotent). */}
            <div className="flex-shrink-0 ps-2 ms-auto" onClick={(e) => e.stopPropagation()}>
                {/* Disabled AND guarded: the row's onClick is the mouse path,
                    this is the keyboard one. Blocking only the row would have
                    left space/enter selecting a show that cannot play. */}
                <RadioButton
                    inputId={id}
                    name="campaign"
                    value={id}
                    disabled={blocked}
                    onChange={() => { if (!blocked) onSelect(id); }}
                    checked={selected}
                />
            </div>
        </div>
    );
});

function AssignCampaignsDropDown({ clearDeviceOnClose = true }) {
    const { t } = useTranslation();
    const assignCampaignDevices = useAssignCampaignDevices();
    const { companyCampaigns, selectedCampaign, selectedDevice } = useSelector((state) => state.device);
    // const { isFetching, data, params } = useSelector((state) => state.popupScroll);
    const lastElementRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState("");

    const { openDialog } = useSelector((state) => state.global);
    const dispatch = useDispatch();
    const [showOptions, setShowOptions] = useState(false)
    const [campaignName, setCampaignName] = useState()
    const [ids, setIds] = useState([]);
    const [selectedId, setSelectedId] = useState(null);


    const {
        data,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        refetch
    } = useGetCampaigns({
        per_page: 10,
        search: searchTerm.trim() || undefined
    });

    // useEffect(() => {
    //     if (lastElementRef.current) {
    //         dispatch(setLastElement(lastElementRef.current));
    //     }
    // }, [lastElementRef.current]);

    const [sameID, setSameID] = useState(true)
    const inputRefs = useRef({});

    // Hold the latest infinite-scroll values in refs so the IntersectionObserver
    // can be created ONCE and never torn down on unrelated re-renders. In
    // react-query v3 `fetchNextPage` gets a new identity every render; reading it
    // from a ref (instead of an effect dep) keeps the observer stable across
    // campaign selections — which previously recreated it and re-fired
    // fetchNextPage, churning the list and resetting scroll to the top.
    const fetchNextRef = useRef(fetchNextPage);
    const hasNextRef = useRef(hasNextPage);
    const isFetchingNextRef = useRef(isFetchingNextPage);
    fetchNextRef.current = fetchNextPage;
    hasNextRef.current = hasNextPage;
    isFetchingNextRef.current = isFetchingNextPage;

    // Re-entrancy guard: react-query's isFetchingNextPage flips async, so several
    // intersection callbacks can fire before it turns true. Lock synchronously the
    // moment we trigger a fetch; release only when a new page actually arrives
    // (pages length grows). Prevents stacked / duplicate page loads from any stray
    // intersection (defense-in-depth on top of the focus-scroll fix).
    const inFlightRef = useRef(false);
    useEffect(() => { inFlightRef.current = false; }, [data?.pages?.length]);

    // Separate callback refs for the scroll container (root) and the sentinel.
    // React attaches child refs BEFORE parent refs, so creating the observer
    // inside the sentinel ref alone read a still-null scroll root → the observer
    // fell back to root:null (the viewport) and never fired on inner-container
    // scroll (infinite scroll broke). Building it only once BOTH nodes are set
    // guarantees the correct `root` regardless of attach order. The handler reads
    // ref-held values so it always sees fresh fetchNextPage/hasNextPage without
    // recreating the observer on selection.
    const observerRef = useRef(null);
    const rootElRef = useRef(null);
    const sentinelElRef = useRef(null);

    const setupObserver = useCallback(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        const root = rootElRef.current;
        const sentinel = sentinelElRef.current;
        if (!root || !sentinel) return; // wait until both are mounted
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextRef.current && !isFetchingNextRef.current && !inFlightRef.current) {
                    inFlightRef.current = true;
                    fetchNextRef.current?.();
                }
            },
            { root, rootMargin: '200px 0px 200px 0px', threshold: 0 }
        );
        io.observe(sentinel);
        observerRef.current = io;
    }, []);

    const scrollCallbackRef = useCallback((node) => {
        rootElRef.current = node;
        setupObserver();
    }, [setupObserver]);

    const sentinelCallbackRef = useCallback((node) => {
        sentinelElRef.current = node;
        setupObserver();
    }, [setupObserver]);


    // Stable array reference while the query data is unchanged — selection
    // re-renders won't rebuild it.
    const campaigns = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

    // Stable selection handler so memoized rows don't re-render on every parent render.
    const handleSelect = useCallback((id) => setSelectedId(id), []);

    const assign = async () => {
        if (!selectedId) return;

        const data = {
            campaign_id: selectedId,
            devices_ids: [selectedDevice?.id],
            type: "devices",
        };

        await assignCampaignDevices.mutateAsync(data, {
            onSuccess: () => {
                dispatch(toggleDialog("assignCampaignsDropDown"));
                // Caller (e.g. the device show page) may own selectedDevice and
                // need it to persist after the modal closes.
                if (clearDeviceOnClose) dispatch(setSelectedDevice({}))
            },
        });
    }

    const onClose = () => {
        dispatch(toggleDialog("assignCampaignsDropDown"))
        if (clearDeviceOnClose) dispatch(setSelectedDevice({}))

    }

    useEffect(() => {
        if (selectedDevice?.campaigns?.length > 0) {
             setSelectedId(selectedDevice.campaigns[0]?.id);
        }

    }, [selectedDevice])

    useEffect(() => {
        if (selectedDevice?.campaigns?.length > 0) {
            if (selectedDevice.campaigns[0]?.id === selectedId) {
                setSameID(true)
            } else {
                setSameID(false)
            }
        }
    }, [selectedId])


    const header = () => {
        return (
            <>
                <h4 className='mb-2'>{t('devices.assign_campaign_to_device')}</h4>
                <p className="text-muted fs-6 mb-0">{t('devices.assign_device_label')}: {selectedDevice.name}</p>
            </>
        )
    }


    const renderFooter = () => {

        return (
            <div className="  text-center d-flex  align-items-end  justify-content-between border-gray h-100">
                <div className="my-2 flex justify-center h-100">
                    {/* {selectedDevice.campaigns[0]?.id === selectedId && (
                        <div className="text-danger fw-semibold fs-7 text-center">
                            The selected campaign is already assigned to this device.
                        </div>
                    )} */}
                </div>
                <div className="">
                    <Button className="  me-3 w-auto p-button-secondary p-button-sm" label={t('buttons.cancel')}
                        disabled={assignCampaignDevices.isLoading}
                        onClick={onClose} />
                    <Button className="  me-3 w-auto p-button-sm" label={t('buttons.save')}
                        loading={assignCampaignDevices.isLoading}
                        // disabled={selectedDevice.campaigns[0]?.id === selectedId}
                        onClick={assign} />
                </div>
            </div>
        );
    }


    const handleInput = () => setSearchTerm(searchTerm.trim());
    const resetInput = () => setSearchTerm("");
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleInput();
        }
    };

    return (
        <>
            <Dialog
                id='assignDeviceDialog'
                footer={renderFooter}

                visible={openDialog.assignCampaignsDropDown}
                style={{ width: '95vw', maxWidth: '680px' }}
                header={header}
                modal className="p-fluid"
                onHide={onClose}>

                <div className="d-flex device_assign_container border-top border-bottom">
                    <div className="w-100 d-flex flex-column justify-start">
                        <div className="d-flex flex-wrap justify-content-center px-1">
                            <div className="w-100 gap-2  m-3 flex justify-between items-center ">


                                <div className="relative flex w-full justify-start   ">
                                    <input
                                        type="text"
                                        className="form-control h-[45px] w-8/12 md:w-10/12 hover:border-[#8e90f5]"
                                        placeholder={t('common.search_placeholder')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                    // disabled={isLoading}
                                    />
                                    {!isEmpty(searchTerm) && (
                                        <Button
                                            icon="pi pi-times"
                                            rounded
                                            text
                                            severity="primery"
                                            aria-label={t('buttons.cancel')}
                                            onClick={resetInput}
                                            className={`absolute inset-y-0 ${(isFetchingNextPage || isLoading) ? "right-15" : "right-10"}  border-transparent bg-transparent text-black`}
                                        />
                                    )}
                                    {(isFetchingNextPage || isLoading) && (
                                        <div className=" inset-y-0 right-10 absolute flex justify-end items-center h-full">
                                            <ProgressSpinner style={{ width: "30px", height: "30px" }} />
                                        </div>
                                    )}
                                    <Button
                                        icon="pi pi-search"
                                        rounded
                                        text
                                        severity="primery"
                                        aria-label={t('buttons.search')}
                                        className="absolute inset-y-0 right-2 border-transparent bg-transparent text-black"
                                        onClick={handleInput}
                                        loading={isLoading}
                                    />

                                </div>


                            </div>
                            <hr className="w-100 mt-0" />

                            {/* <div className="  w-100" style={{ maxHeight: '400px' }}> */}

                            <div className="flex flex-col gap-2 w-100 ">

                                {isLoading ? (
                                    <div className="flex justify-center items-center h-full">
                                        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                                    </div>
                                ) : campaigns.length === 0 ? (
                                    <h5 className="py-5 text-center w-full">
                                        {isEmpty(searchTerm) ? "" : t('messages.no_results')}
                                    </h5>
                                ) : (


                                    <div ref={scrollCallbackRef} style={{ maxHeight: '55vh' }}
                                        className={`  d-flex flex-column   scroll_div  overflow-y-auto overflow-x-hidden px-3 w-100`}>

                                        <div className="flex flex-col gap-2 pb-2 w-100">
                                            {campaigns?.map((item, index) => (
                                                <CampaignRow
                                                    key={item.id ?? index}
                                                    item={item}
                                                    selected={selectedId === String(item.id)}
                                                    onSelect={handleSelect}
                                                    t={t}
                                                />
                                            ))}
                                        </div>


                                        <div ref={sentinelCallbackRef} className="h-1 w-full" />
                                    </div>

                                )}
                            </div>
                            {/* </div> */}

                        </div>
                    </div>
                </div >


            </Dialog >
        </>
    )
}

export default AssignCampaignsDropDown